import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Gavel as GavelIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import { projectService, RISK_CATEGORY_LABELS } from '../services/api/projectService';
import { checkItemService, type CategoryInfo } from '../services/api/checkItemService';
import { aiAdviceService, type AIAdviceResponse, type Countermeasure } from '../services/api/aiAdviceService';
import type { Project, ProjectDetail, CheckItem, RiskCategory, CheckStatus } from '../types';

// Risk category colors
const RISK_CATEGORY_COLORS: Record<RiskCategory, string> = {
  legal: '#1976d2',
  economic: '#2e7d32',
  financial: '#ed6c02',
  international: '#9c27b0',
  domestic_political: '#d32f2f',
  reputation: '#0288d1',
  employee: '#7b1fa2',
  labor_union: '#c2185b',
};

// Priority labels
const PRIORITY_LABELS = {
  high: { label: '高', color: 'error' as const },
  medium: { label: '中', color: 'warning' as const },
  low: { label: '低', color: 'info' as const },
};

// Risk score calculation
function calculateRiskScore(
  checkItems: CheckItem[],
  checkResults: Map<string, CheckStatus>,
  _categories: CategoryInfo[]
): number {
  if (checkItems.length === 0) return 0;

  let totalWeight = 0;
  let completedWeight = 0;

  checkItems.forEach((item) => {
    const priorityWeight = item.priority === 'high' ? 3 : item.priority === 'medium' ? 2 : 1;
    totalWeight += priorityWeight;

    const status = checkResults.get(item.id);
    if (status === 'completed' || status === 'not_applicable') {
      completedWeight += priorityWeight;
    }
  });

  if (totalWeight === 0) return 0;
  return Math.round((completedWeight / totalWeight) * 100);
}

// Risk level calculation
function getRiskLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: '低リスク', color: '#2e7d32' };
  if (score >= 60) return { label: '中リスク', color: '#ed6c02' };
  if (score >= 40) return { label: '要注意', color: '#f57c00' };
  return { label: '高リスク', color: '#d32f2f' };
}

// Category analysis type
interface CategoryAnalysis {
  category: RiskCategory;
  categoryName: string;
  total: number;
  completed: number;
  percentage: number;
  color: string;
}

// Unchecked high priority item type
interface UncheckedHighPriorityItem {
  id: string;
  name: string;
  categoryName: string;
  riskCategory: RiskCategory;
}

export function AnalysisDashboardPage() {
  // Project selection state
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Data state
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [checkItems, setCheckItems] = useState<CheckItem[]>([]);

  // Loading state
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingMaster, setIsLoadingMaster] = useState(true);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // AI Advice state
  const [isAdviceDialogOpen, setIsAdviceDialogOpen] = useState(false);
  const [selectedRiskItem, setSelectedRiskItem] = useState<UncheckedHighPriorityItem | null>(null);
  const [aiAdvice, setAiAdvice] = useState<AIAdviceResponse | null>(null);
  const [isLoadingAdvice, setIsLoadingAdvice] = useState(false);
  const [adviceError, setAdviceError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    countermeasures: true,
    cautions: true,
    nextActions: true,
    relatedLaws: false,
  });

  // Fetch projects list
  const fetchProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    try {
      const data = await projectService.getProjects();
      setProjects(data);
      // Auto-select first project if available
      if (data.length > 0 && !selectedProjectId) {
        setSelectedProjectId(data[0].id);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '案件一覧の取得に失敗しました';
      setError(message);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [selectedProjectId]);

  // Fetch master data (categories and check items)
  const fetchMasterData = useCallback(async () => {
    setIsLoadingMaster(true);
    try {
      const [categoriesData, itemsData] = await Promise.all([
        checkItemService.getCategories(),
        checkItemService.getCheckItems(),
      ]);
      setCategories(categoriesData);
      setCheckItems(itemsData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'マスターデータの取得に失敗しました';
      setError(message);
    } finally {
      setIsLoadingMaster(false);
    }
  }, []);

  // Fetch project detail
  const fetchProjectDetail = useCallback(async (projectId: string) => {
    if (!projectId) return;

    setIsLoadingDetail(true);
    setError(null);
    try {
      const data = await projectService.getProject(projectId);
      setProjectDetail(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : '案件詳細の取得に失敗しました';
      setError(message);
      setProjectDetail(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProjects();
    fetchMasterData();
  }, [fetchProjects, fetchMasterData]);

  // Fetch project detail when selection changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchProjectDetail(selectedProjectId);
    }
  }, [selectedProjectId, fetchProjectDetail]);

  // Create check results map
  const checkResultsMap = useMemo(() => {
    const map = new Map<string, CheckStatus>();
    projectDetail?.checkResults?.forEach((result) => {
      map.set(result.checkItemId, result.status);
    });
    return map;
  }, [projectDetail]);

  // Calculate overall progress
  const overallProgress = useMemo(() => {
    if (checkItems.length === 0) return { total: 0, completed: 0, percentage: 0 };

    const total = checkItems.length;
    let completed = 0;

    checkItems.forEach((item) => {
      const status = checkResultsMap.get(item.id);
      if (status === 'completed' || status === 'not_applicable') {
        completed++;
      }
    });

    return {
      total,
      completed,
      percentage: Math.round((completed / total) * 100),
    };
  }, [checkItems, checkResultsMap]);

  // Calculate category analysis
  const categoryAnalysis = useMemo((): CategoryAnalysis[] => {
    const categoryMap = new Map<RiskCategory, { total: number; completed: number }>();

    // Initialize all categories
    categories.forEach((cat) => {
      if (!categoryMap.has(cat.riskCategory)) {
        categoryMap.set(cat.riskCategory, { total: 0, completed: 0 });
      }
    });

    // Count items by category
    checkItems.forEach((item) => {
      const category = categories.find((c) => c.id === item.categoryId);
      if (category) {
        const current = categoryMap.get(category.riskCategory) || { total: 0, completed: 0 };
        current.total++;

        const status = checkResultsMap.get(item.id);
        if (status === 'completed' || status === 'not_applicable') {
          current.completed++;
        }

        categoryMap.set(category.riskCategory, current);
      }
    });

    // Convert to array
    return Array.from(categoryMap.entries())
      .map(([riskCategory, data]) => ({
        category: riskCategory,
        categoryName: RISK_CATEGORY_LABELS[riskCategory],
        total: data.total,
        completed: data.completed,
        percentage: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
        color: RISK_CATEGORY_COLORS[riskCategory],
      }))
      .filter((item) => item.total > 0)
      .sort((a, b) => a.percentage - b.percentage);
  }, [categories, checkItems, checkResultsMap]);

  // Calculate risk score
  const riskScore = useMemo(() => {
    return calculateRiskScore(checkItems, checkResultsMap, categories);
  }, [checkItems, checkResultsMap, categories]);

  const riskLevel = useMemo(() => getRiskLevel(riskScore), [riskScore]);

  // Get unchecked high priority items
  const uncheckedHighPriorityItems = useMemo((): UncheckedHighPriorityItem[] => {
    return checkItems
      .filter((item) => {
        const status = checkResultsMap.get(item.id);
        return item.priority === 'high' && (!status || status === 'unchecked');
      })
      .map((item) => {
        const category = categories.find((c) => c.id === item.categoryId);
        return {
          id: item.id,
          name: item.name,
          categoryName: category?.name || '不明',
          riskCategory: category?.riskCategory || 'legal',
        };
      })
      .slice(0, 10);
  }, [checkItems, checkResultsMap, categories]);

  // Loading state check
  const isLoading = isLoadingProjects || isLoadingMaster || isLoadingDetail;

  // AI Advice handlers
  const handleOpenAdviceDialog = useCallback(async (item: UncheckedHighPriorityItem) => {
    setSelectedRiskItem(item);
    setIsAdviceDialogOpen(true);
    setAiAdvice(null);
    setAdviceError(null);
    setIsLoadingAdvice(true);

    try {
      const advice = await aiAdviceService.getRiskAdvice(selectedProjectId, item.id);
      setAiAdvice(advice);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'AI対応策の取得に失敗しました';
      setAdviceError(message);
    } finally {
      setIsLoadingAdvice(false);
    }
  }, [selectedProjectId]);

  const handleCloseAdviceDialog = useCallback(() => {
    setIsAdviceDialogOpen(false);
    setSelectedRiskItem(null);
    setAiAdvice(null);
    setAdviceError(null);
  }, []);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Priority color helper
  const getPriorityColor = (priority: Countermeasure['priority']) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  // Render AI Advice Dialog
  const renderAdviceDialog = () => (
    <Dialog
      open={isAdviceDialogOpen}
      onClose={handleCloseAdviceDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AutoAwesomeIcon color="primary" />
        <Box>
          <Typography variant="h6">AI対応策提案</Typography>
          {selectedRiskItem && (
            <Typography variant="body2" color="text.secondary">
              {selectedRiskItem.name}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleCloseAdviceDialog}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadingAdvice && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={48} />
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              AIが対応策を分析中...
            </Typography>
          </Box>
        )}

        {adviceError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {adviceError}
          </Alert>
        )}

        {aiAdvice && !isLoadingAdvice && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary */}
            <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                概要
              </Typography>
              <Typography variant="body2">{aiAdvice.summary}</Typography>
            </Paper>

            {/* Countermeasures */}
            <Box>
              <Box
                onClick={() => toggleSection('countermeasures')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayArrowIcon color="success" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    対応策 ({aiAdvice.countermeasures.length}件)
                  </Typography>
                </Box>
                {expandedSections.countermeasures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              <Collapse in={expandedSections.countermeasures}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {aiAdvice.countermeasures.map((cm, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {index + 1}. {cm.title}
                        </Typography>
                        <Chip
                          label={cm.priority === 'high' ? '高' : cm.priority === 'medium' ? '中' : '低'}
                          color={getPriorityColor(cm.priority)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {cm.description}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        想定工数: {cm.estimatedEffort}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Collapse>
            </Box>

            {/* Cautions */}
            {aiAdvice.cautions.length > 0 && (
              <Box>
                <Box
                  onClick={() => toggleSection('cautions')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      注意事項 ({aiAdvice.cautions.length}件)
                    </Typography>
                  </Box>
                  {expandedSections.cautions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={expandedSections.cautions}>
                  <List dense>
                    {aiAdvice.cautions.map((caution, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <InfoIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={caution} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {/* Next Actions */}
            {aiAdvice.nextActions.length > 0 && (
              <Box>
                <Box
                  onClick={() => toggleSection('nextActions')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="info" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      次のアクション ({aiAdvice.nextActions.length}件)
                    </Typography>
                  </Box>
                  {expandedSections.nextActions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={expandedSections.nextActions}>
                  <List dense>
                    {aiAdvice.nextActions.map((action, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <PlayArrowIcon color="info" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={action} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {/* Related Laws */}
            {aiAdvice.relatedLaws.length > 0 && (
              <Box>
                <Box
                  onClick={() => toggleSection('relatedLaws')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon color="secondary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      関連法令 ({aiAdvice.relatedLaws.length}件)
                    </Typography>
                  </Box>
                  {expandedSections.relatedLaws ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={expandedSections.relatedLaws}>
                  <List dense>
                    {aiAdvice.relatedLaws.map((law, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <GavelIcon color="secondary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={law} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            )}

            {/* AI Generated Notice */}
            {aiAdvice.isAIGenerated === false && (
              <Alert severity="info" sx={{ mt: 2 }}>
                この提案はAI機能が無効のため、一般的なアドバイスです。
                より詳細な提案を受けるには、ANTHROPIC_API_KEYを設定してください。
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseAdviceDialog}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );

  // Render progress circle (CSS-based pie chart)
  const renderProgressCircle = (percentage: number, size: number = 120) => {
    const strokeWidth = 10;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <Box sx={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#1976d2"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h4" fontWeight={600}>
            {percentage}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            完了率
          </Typography>
        </Box>
      </Box>
    );
  };

  // Render risk score gauge
  const renderRiskScoreGauge = (score: number) => {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            width: 140,
            height: 70,
            margin: '0 auto',
            overflow: 'hidden',
          }}
        >
          {/* Background semi-circle */}
          <Box
            sx={{
              position: 'absolute',
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: `conic-gradient(
                #d32f2f 0deg 72deg,
                #f57c00 72deg 108deg,
                #ed6c02 108deg 144deg,
                #2e7d32 144deg 180deg,
                #e0e0e0 180deg 360deg
              )`,
            }}
          />
          {/* Inner circle (mask) */}
          <Box
            sx={{
              position: 'absolute',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'white',
              top: 20,
              left: 20,
            }}
          />
          {/* Needle */}
          <Box
            sx={{
              position: 'absolute',
              width: 4,
              height: 40,
              background: '#333',
              bottom: 0,
              left: '50%',
              transformOrigin: 'bottom center',
              transform: `translateX(-50%) rotate(${(score / 100) * 180 - 90}deg)`,
              borderRadius: 2,
              transition: 'transform 0.5s ease',
            }}
          />
          {/* Center dot */}
          <Box
            sx={{
              position: 'absolute',
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: '#333',
              bottom: -6,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
        </Box>
        <Typography variant="h5" fontWeight={600} sx={{ mt: 1, color: riskLevel.color }}>
          {score}点
        </Typography>
        <Chip
          label={riskLevel.label}
          size="small"
          sx={{
            mt: 1,
            backgroundColor: riskLevel.color,
            color: 'white',
            fontWeight: 500,
          }}
        />
      </Box>
    );
  };

  // Render horizontal bar chart
  const renderBarChart = (data: CategoryAnalysis[]) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map((item) => (
          <Box key={item.category}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {item.categoryName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.completed}/{item.total} ({item.percentage}%)
              </Typography>
            </Box>
            <Box
              sx={{
                height: 12,
                borderRadius: 1,
                backgroundColor: '#e0e0e0',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${item.percentage}%`,
                  backgroundColor: item.color,
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Render radar chart (CSS-based)
  const renderRadarChart = (data: CategoryAnalysis[]) => {
    if (data.length === 0) return null;

    const size = 280;
    const center = size / 2;
    const maxRadius = 100;
    const levels = 5;

    // Calculate points for each category
    const angleStep = (2 * Math.PI) / data.length;
    const points = data.map((item, index) => {
      const angle = angleStep * index - Math.PI / 2;
      const radius = (item.percentage / 100) * maxRadius;
      return {
        x: center + radius * Math.cos(angle),
        y: center + radius * Math.sin(angle),
        labelX: center + (maxRadius + 30) * Math.cos(angle),
        labelY: center + (maxRadius + 30) * Math.sin(angle),
        ...item,
      };
    });

    const pathData = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ') + ' Z';

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <svg width={size} height={size}>
          {/* Background grid circles */}
          {Array.from({ length: levels }, (_, i) => {
            const r = ((i + 1) / levels) * maxRadius;
            return (
              <circle
                key={i}
                cx={center}
                cy={center}
                r={r}
                fill="none"
                stroke="#e0e0e0"
                strokeWidth={1}
              />
            );
          })}

          {/* Axis lines */}
          {points.map((_, index) => {
            const angle = angleStep * index - Math.PI / 2;
            const endX = center + maxRadius * Math.cos(angle);
            const endY = center + maxRadius * Math.sin(angle);
            return (
              <line
                key={index}
                x1={center}
                y1={center}
                x2={endX}
                y2={endY}
                stroke="#e0e0e0"
                strokeWidth={1}
              />
            );
          })}

          {/* Data polygon */}
          <path
            d={pathData}
            fill="rgba(25, 118, 210, 0.3)"
            stroke="#1976d2"
            strokeWidth={2}
          />

          {/* Data points */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r={5}
              fill={point.color}
              stroke="white"
              strokeWidth={2}
            />
          ))}

          {/* Labels */}
          {points.map((point, index) => (
            <text
              key={index}
              x={point.labelX}
              y={point.labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={11}
              fill="#666"
            >
              {point.categoryName.replace('リスク', '')}
            </text>
          ))}
        </svg>
      </Box>
    );
  };

  // Loading skeleton
  const renderLoadingSkeleton = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight={600}>
              リスク分析ダッシュボード
            </Typography>
          </Box>
        </Box>

        {/* Project Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>案件を選択</InputLabel>
            <Select
              value={selectedProjectId}
              label="案件を選択"
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={isLoadingProjects}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name} - {project.targetCompany}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* No project selected */}
        {!selectedProjectId && !isLoadingProjects && projects.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            分析する案件がありません。まず案件を作成してください。
          </Alert>
        )}

        {/* Dashboard Content */}
        {isLoading && selectedProjectId ? (
          renderLoadingSkeleton()
        ) : selectedProjectId && projectDetail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Top Row: Summary Cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                gap: 3,
              }}
            >
              {/* Overall Progress Card */}
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    チェック進捗
                  </Typography>
                  {renderProgressCircle(overallProgress.percentage)}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {overallProgress.completed} / {overallProgress.total} 項目完了
                  </Typography>
                </CardContent>
              </Card>

              {/* Risk Score Card */}
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    リスクスコア
                  </Typography>
                  {renderRiskScoreGauge(riskScore)}
                </CardContent>
              </Card>

              {/* Status Count Cards */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    ステータス別件数
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                        <Typography variant="body2">完了</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {checkItems.filter((item) => checkResultsMap.get(item.id) === 'completed').length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ed6c02' }} />
                        <Typography variant="body2">要確認</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {checkItems.filter((item) => checkResultsMap.get(item.id) === 'needs_review').length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e' }} />
                        <Typography variant="body2">未確認</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {checkItems.filter((item) => {
                          const status = checkResultsMap.get(item.id);
                          return !status || status === 'unchecked';
                        }).length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#757575' }} />
                        <Typography variant="body2">該当なし</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {checkItems.filter((item) => checkResultsMap.get(item.id) === 'not_applicable').length}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Priority Distribution Card */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    優先度別件数
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    {(['high', 'medium', 'low'] as const).map((priority) => {
                      const count = checkItems.filter((item) => item.priority === priority).length;
                      const completed = checkItems.filter((item) => {
                        const status = checkResultsMap.get(item.id);
                        return item.priority === priority && (status === 'completed' || status === 'not_applicable');
                      }).length;
                      return (
                        <Box key={priority} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            label={PRIORITY_LABELS[priority].label}
                            color={PRIORITY_LABELS[priority].color}
                            size="small"
                          />
                          <Typography variant="body2">
                            {completed} / {count}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Middle Row: Charts */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                gap: 3,
              }}
            >
              {/* Category Bar Chart */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <TrendingUpIcon color="primary" />
                    <Typography variant="h6">リスクカテゴリ別進捗</Typography>
                  </Box>
                  {categoryAnalysis.length > 0 ? (
                    renderBarChart(categoryAnalysis)
                  ) : (
                    <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      データがありません
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Radar Chart */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AssessmentIcon color="primary" />
                    <Typography variant="h6">カテゴリ別完了率レーダー</Typography>
                  </Box>
                  {categoryAnalysis.length >= 3 ? (
                    renderRadarChart(categoryAnalysis)
                  ) : (
                    <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      レーダーチャートの表示には3つ以上のカテゴリが必要です
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Bottom Row: High Priority Items */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="error" />
                  <Typography variant="h6">未対応の高優先度項目</Typography>
                  <Chip
                    label={`${uncheckedHighPriorityItems.length}件`}
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                {uncheckedHighPriorityItems.length > 0 ? (
                  <List dense>
                    {uncheckedHighPriorityItems.map((item) => (
                      <ListItem
                        key={item.id}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: 'rgba(211, 47, 47, 0.05)',
                          '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' },
                        }}
                        secondaryAction={
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleOpenAdviceDialog(item)}
                            sx={{ ml: 2 }}
                          >
                            対応策を提案
                          </Button>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <WarningIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={item.categoryName}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: RISK_CATEGORY_COLORS[item.riskCategory],
                                  color: 'white',
                                }}
                              />
                              <Chip
                                label="高優先度"
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      py: 4,
                    }}
                  >
                    <CheckCircleIcon color="success" />
                    <Typography color="text.secondary">
                      高優先度の未対応項目はありません
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : null}
      </Box>

      {/* AI Advice Dialog */}
      {renderAdviceDialog()}
    </MainLayout>
  );
}
